import { Images } from "Components/Images";
import StudentRenderer from "Components/ModelRenderers/StudentRenderer";
import MultiStudentPicker from "Components/MultiStudentPicker";
import { MyTable } from "Components/MyTable";
import RetainOverlayTrigger from "Components/RetainOverlayTrigger";
import StudentFilterItem from "Components/StudentFilterItem";
import { BOND_TABLE } from "Constants/Bond";
import { GENERIC_GIFT_TAGS } from "Constants/Common";
import useSchaleStudents from "Hooks/useStudents";
import IStyle from "Interfaces/IStyle";
import { ITableRow } from "Interfaces/ITableRow";
import ImageGallery from "Modals/ImageGallery";
import { SchaleStudent } from "Models/Student";
import StudentFilter from "Models/StudentFilter";
import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { getJson } from "Utils/Common";

const KEYS = {
	ITEMS: "midokuni.tool.favor.items",
	CRAFT_DETAIL: "midokuni.tool.favor.craftDetail",
	GIFT_CTR: "midokuni.tool.favor.giftCtr",
	BOND_LV: "midokuni.tool.favor.bondLv",
	BOND_GOAL: "midokuni.tool.favor.bondGoal",
}
interface ISchaleBaseItem {
	Id: number;
	Icon: string;
	Quality: number;
}

interface ISchaleItem extends ISchaleBaseItem {
	Name: string;
	Desc: string;
	Category: string;
	Flavor: string;
	Effect: string;
	Rarity: string;
}

interface ISchaleGiftItem extends ISchaleItem {
	Category: "Favor";
	ExpValue: number;
	Tags: string[];
}

interface ISchaleCraftGroup {
	GroupId: number;
	Weight: number;
}

interface ISchaleCraftNode extends ISchaleBaseItem {
	IsReleased: [
		jp: boolean,
		global: boolean,
		cn: boolean,
	];
	Tier: number;
	Quality: number;
	NameEn: string;
	NameJp: string;
	DescEn: string;
	DescJp: string;
	// There are also Kr Tw Th Cn Zh Vi etc but excluding them
	Groups: ISchaleCraftGroup[];
	Property: number;
	PropertyGlobal?: number;
	PropertyCn?: number;
	ChanceJp: number;
	ChanceGlobal: number;
	ChanceCn: number;
}

interface ISchaleCraftJson {
	Nodes: ISchaleCraftNode[];
	TotalProp: {
		jp: [number,number,number,...number[]];
		global: [number,number,number,...number[]];
		cn: [number,number,number,...number[]];
	}
}

interface IBondCraftNode extends ISchaleCraftNode {
	AverageBondExp: number;
	AverageBondExpGlobal?: number;
}

interface IBondCraft extends ISchaleCraftJson {
	Nodes: IBondCraftNode[];
}

interface ISchaleGroupItem {
	Id: number;
	Type: string;
	Chance: number;
	AmountMin: number;
	AmountMax: number;
}

interface ISchaleGroupEntry {
	Id?: number;
	Items: ISchaleGroupItem[];
	ItemsGlobal?: ISchaleGroupItem[];
	ItemsCn?: ISchaleGroupItem[];
}

interface IStudentBondGroup extends ISchaleGroupEntry {
	Id: number;
	AverageBondExp: number;
	AverageBondExpGlobal?: number;
}

interface ISchaleGroupsJson {
	[groupId: string]: ISchaleGroupEntry;
}
const styles:IStyle = {
	cardContainer: {
		display: "flex", 
		flexDirection: "row", 
		flexWrap: "wrap"
	},
	cardTitle: {
		overflow: "hidden",
		height: "2em",
	}
}

function calculateBaseGiftExp(gift:ISchaleGiftItem) {
	const genericTagCount = gift.Tags.filter(x => GENERIC_GIFT_TAGS.includes(x)).length;
	const baseFavorGrade = 1 + genericTagCount;

	return gift.ExpValue * baseFavorGrade
}

function calculateGiftExp(gift:ISchaleGiftItem, student:SchaleStudent) {
	const allTags = [...student.FavorItemTags, ...student.FavorItemUniqueTags, ...GENERIC_GIFT_TAGS];
	const commonTags = gift.Tags.filter(x => allTags.includes(x));
	const favorGrade = Math.min(commonTags.length, 3);
	const multiplier = 1+favorGrade;
	const exp = gift.ExpValue * multiplier

	return exp;
}

function getIcon(data:ISchaleItem) {
	return `https://schaledb.com/images/item/icon/${data.Icon}.webp`;
}

function getCraftIcon(data:ISchaleCraftNode) {
	return `https://schaledb.com/images/craftnode/${data.Icon}.png`;
}
interface IStudentBondPanelProps {
	student: SchaleStudent;
	index: number;
	craft: ISchaleCraftJson;
	groups: ISchaleGroupsJson;
	gifts: ISchaleGiftItem[];
	genericGifts?: ISchaleGiftItem[];
	showCraftDetail?: boolean;
	onClose: () => void;
}

const BondCraftNode = ({node}:{node:IBondCraftNode}) => {
	if (node.AverageBondExp === 0 && node.AverageBondExpGlobal === 0) return null;
	const aveJp = (node.AverageBondExp).toFixed(2);
	const aveGlobal = (node.AverageBondExpGlobal).toFixed(2);
	return <Col style={{display: "inline-block", alignContent: "center", textAlign: "center", maxWidth: "4rem", overflow: "hidden"}}>
		<Row><Col className={`quality-${node.Quality}`}>
			<RetainOverlayTrigger
				placement="bottom"
				overlay={<span id={`craft-tooltip`}>{node.NameEn}</span>}>
					<span><Images.Icon imgUrl={getCraftIcon(node)} size="4rem" alt={node.NameEn} /></span>
			</RetainOverlayTrigger>
		</Col></Row>
		<Row style={{marginTop: "-0.5rem", whiteSpace: "nowrap"}}><Col><small>{node.NameEn}</small></Col></Row>
		<Row style={{marginTop: "-0.5rem"}}><Col><small>{aveJp === aveGlobal ? aveJp : `G${aveGlobal} J${aveJp}`}</small></Col></Row>
		
		{/* JP:  */}
		{/* {(100*node.ChanceJp).toFixed(2)}% =&gt;  */}
		{/* {(100*node.Property / craft.TotalProp.jp[node.Tier-1]).toFixed(2)} |  */}
		{/* JP: {(node.ChanceJp*node.AverageBondExp).toFixed(3)} */}
		{/* J: {(node.AverageBondExp).toFixed(2)} */}
		{/* <br></br>  */}
		{/* Global:  */}
		{/* {(100*node.ChanceGlobal).toFixed(2)}% =&gt;  */}
		{/* {(100*node.Property / craft.TotalProp.global[node.Tier-1]).toFixed(2)} |  */}
		{/* G: {(node.AverageBondExpGlobal).toFixed(2)} */}
		{/* Global: {(node.ChanceGlobal*node.AverageBondExpGlobal).toFixed(3)} */}
		{/* <br></br> */}
		{/* {node.Groups.map((group:ISchaleCraftGroup)=>{
			const groupData = relevantGroups.find(g=>g.Id === group.GroupId);
			if (groupData) {
				return <>{groupData.Id}: {groupData.AverageBondExp.toFixed(2)} =&gt; {(groupData.AverageBondExp*group.Weight/node.Property).toFixed(2)}<br></br></>
			}
			return <></>
		})} */}
	</Col>
}

function StudentBondPanel(props: IStudentBondPanelProps) {
	const {student, index, onClose, craft, groups} = props;
	const showCraftDetail = props.showCraftDetail ?? true;
	const [bond, _setBond] = useState<number>(
		Math.min(100, Math.max(1, parseInt(localStorage.getItem(`${KEYS.BOND_LV}.${student.id}`) ?? "1")))
	);
	const [goal, _setGoal] = useState<number>(Math.min(100, Math.max(1, parseInt(localStorage.getItem(`${KEYS.BOND_GOAL}.${student.id}`) ?? "100"))));
	const [giftCtr, _setGiftCtr] = useState<{
		[id: number]: number
	}>(
		JSON.parse(localStorage.getItem(`${KEYS.GIFT_CTR}.${student.id}`) ?? null) ?? {}
	);
	function setGiftCtr(toSet) {
		_setGiftCtr(toSet);
		localStorage.setItem(`${KEYS.GIFT_CTR}.${student.id}`, JSON.stringify(toSet));
	}
	function setBond(toSet:number) {
		_setBond(toSet);
		localStorage.setItem(`${KEYS.BOND_LV}.${student.id}`, toSet.toString());
	}
	function setGoal(toSet:number) {
		_setGoal(toSet);
		localStorage.setItem(`${KEYS.BOND_GOAL}.${student.id}`, toSet.toString());
	}
	const studentTags = [...student.FavorItemTags, ...student.FavorItemUniqueTags];
	const gifts = useMemo(()=>(props.gifts?.filter(gift=>gift.Tags.filter(gtag=>studentTags.indexOf(gtag)>=0).length>0) ?? [])
		.sort((a,b)=>calculateGiftExp(b, student) - calculateGiftExp(a, student)),[props.gifts, student.id]);
		
	const aveGroupBondReducer = (acc: number, curr: ISchaleGroupItem) => {
		const gift = props.gifts.find(g=>g.Id === curr.Id);
		if (gift) {
			return acc + (calculateGiftExp(gift, student) * curr.Chance * (curr.AmountMin + curr.AmountMax) / 2);
		}
		return acc;
	}
	const relevantGroupsJp = useMemo(()=>(groups ? Object.entries(groups)
		.map(([key, group])=>{
			return {
				...group,
				Id: parseInt(key),
				AverageBondExp: group.Items.reduce(aveGroupBondReducer, 0),
			} as IStudentBondGroup
		}) : [])
	, [groups, gifts]);
	const relevantGroupsGlobal = useMemo(()=>(!!!groups?.ItemsGlobal ? relevantGroupsJp : groups ? Object.entries(groups)
		.map(([key, group])=>{
			return {
				...group,
				Id: parseInt(key),
				AverageBondExp: group?.ItemsGlobal.reduce(aveGroupBondReducer, 0),
			} as IStudentBondGroup
		}) : [])
	, [groups, gifts]);
	const relevantGroups = useMemo(()=>{
		const ids = new Set([...relevantGroupsJp.map(g=>g.Id), ...relevantGroupsGlobal.map(g=>g.Id)]);
		const toReturnGroups = [];
		for (const id of ids) {
			const groupJp = relevantGroupsJp.find(g=>g.Id === id);
			const groupGlobal = relevantGroupsGlobal.find(g=>g.Id === id);
			let data:IStudentBondGroup;
			if (groupJp) {
				data = {...groupJp} as IStudentBondGroup;
				if (groupGlobal) {
					data.AverageBondExpGlobal = groupGlobal.AverageBondExp;
				}
			} else if (groupGlobal) {
				data = {...groupGlobal} as IStudentBondGroup;
				if (groupGlobal) {
					data.AverageBondExpGlobal = groupGlobal.AverageBondExp;
					data.AverageBondExp = 0;
				}
			}
			if (data.AverageBondExp > 0 || data.AverageBondExpGlobal > 0) {
				toReturnGroups.push(data);
			}
		}

		toReturnGroups.push({
			Id: -1,
			AverageBondExp: 20,
			AverageBondExpGlobal: 20,
			Items: [],
			ItemsGlobal: [],
			ItemsCn: [],
		} as IStudentBondGroup);

		return toReturnGroups;
	}, [relevantGroupsGlobal, relevantGroupsJp]);
	const relevantCrafts = useMemo(()=>{
		if (craft == null) return null;
		const nodes = craft.Nodes.filter((i:ISchaleCraftNode)=>{
			return Object.values(i.Groups)
				.map(g=>g.GroupId)
				.length > 0
		}).map(node => {
			return {
				...node,
				AverageBondExp: node.Groups.reduce((acc, group) => {
					const groupData = relevantGroups.find(g=>g.Id === group.GroupId);
					if (groupData) {
						return acc + (groupData.AverageBondExp*group.Weight/node.Property);
					}
					return acc;
				}, 0),
				AverageBondExpGlobal: node.Groups.reduce((acc, group) => {
					const groupData = relevantGroups.find(g=>g.Id === group.GroupId);
					if (groupData) {
						return acc + (groupData.AverageBondExpGlobal*group.Weight/(node.PropertyGlobal ?? node.Property));
					}
					return acc;
				}, 0),
			}as IBondCraftNode
		});
		const etcGifts = nodes.filter(node => node.Tier === 2 && node.Icon === "Favor" && node.AverageBondExp.toFixed(0) === "20" && node.AverageBondExpGlobal.toFixed(0) === "20")
		.reduce((acc, curr) => {
			acc.ChanceCn += curr.ChanceCn;
			acc.ChanceGlobal += curr.ChanceGlobal;
			acc.ChanceJp += curr.ChanceJp;
			acc.Property += curr.Property;
			acc.Groups.push(...curr.Groups);
			
			return acc;
		}, {...nodes[0], Nodes: [],
			ChanceCn: 0,
			ChanceGlobal: 0,
			ChanceJp: 0,
			Property: 0,
			Groups: [],
			AverageBondExp: 20,
			AverageBondExpGlobal: 20,
			Icon: "Favor",
			IsReleased: [true, true, true],
			Tier: 2,
			NameEn: "Etc",
			DescEn: "Other gift nodes"
		} as IBondCraftNode);

		return {
			...craft,
			Nodes: [etcGifts,...nodes.filter(node => !(node.Icon === "Favor" && node.AverageBondExp.toFixed(0) === "20" && node.AverageBondExpGlobal.toFixed(0) === "20" && node.Id !== 10))].sort((a,b)=>b.AverageBondExp - a.AverageBondExp),
		}
	}, [craft, relevantGroups]);

	const genericGifts = useMemo<ISchaleGiftItem[]>(()=>{
		if (!!!props.genericGifts) {
			return [];
		}
		const srs = gifts.filter(gift=>gift.Rarity === "SR").map(gift=>calculateGiftExp(gift, student));
		const ssrs = gifts.filter(gift=>gift.Rarity === "SSR").map(gift=>calculateGiftExp(gift, student));
		const gg = [...props.genericGifts].map(i=>{
			const item = {...(i as ISchaleGiftItem)} as ISchaleGiftItem;
			if (item.Id === 100000) {
				item.ExpValue = Math.min(item.ExpValue, ...srs);
			} else if (item.Id === 100009) {
				item.ExpValue = Math.min(item.ExpValue, ...ssrs);
			} else if (item.Id === 100008) {
				item.ExpValue = Math.max(item.ExpValue, ...srs);
			}

			return item;
		});

		return gg;
	} ,[gifts, props.genericGifts, student]);

	const startBondExp = BOND_TABLE[bond];
	const gainBondExp = gifts.map(gift => (giftCtr?.[gift.Id] ?? 0) * calculateGiftExp(gift, student)).reduce((acc, curr) => acc + curr, 0)
		+ genericGifts.map(gift => (giftCtr?.[gift.Id] ?? 0) * calculateGiftExp(gift, student)).reduce((acc, curr) => acc + curr, 0)
	;
	const totalBondExp = startBondExp + gainBondExp;
	const endBondLv = Math.max(Math.min(BOND_TABLE.findIndex((totalExp, i) => totalExp >= totalBondExp || i > 100)-1, 100), 1, bond);
	const goalRemain = BOND_TABLE[goal]-BOND_TABLE[endBondLv];
	const updateGiftAmount = (giftId: number, value: number) => {
		if (value > 0) {
			setGiftCtr({...giftCtr, [giftId]: value});
		} else {
			const temp = {...giftCtr};
			delete temp[giftId];
			setGiftCtr(temp);
		}
	}
	return (
		<Col xxs={12} md="auto" className="student-bond-panel bg-pink-border rounded-border" key={index} style={{border: "1px solid", opacity: "unset", display: "inline-block"}}>
			<Row><Col><StudentFilterItem 
				key={index}
				bg="violet"
				className="list-item student include"
				student={student}
				id={index}
				onClose={onClose} /></Col></Row>
			<Row><Col className="text-pink"><StudentRenderer model={student} compact={2} bare disableSchale/></Col></Row>
			<Row style={{marginTop: "0.5rem"}}>
				<Col>Bond: <input type="number" min={1} max={100} value={bond} style={{width: "2.5rem", padding: 0, background: "rgba(255,255,255,0.1)", alignContent: "center", textAlign: "center", border: "none"}}
					onChange={(e)=>{
						setBond(Math.min(parseInt(e.target.value.replaceAll(/[^\d]/g,"")), 100));
					}}
				></input> &gt; {endBondLv?.toLocaleString()}</Col>
				<Col hidden={endBondLv >= BOND_TABLE.length-2}>
					Next: {((BOND_TABLE[endBondLv+1]-BOND_TABLE[endBondLv]) - (totalBondExp-BOND_TABLE[endBondLv]))?.toLocaleString() }
				</Col>
			</Row>
			<Row className="progress-bar" style={{margin: "0.5rem 0 0 0", padding: "0 0 0 0"}}><Col>
				<div style={{width: "100%", border: "1px solid", height: "0.5rem"}}>
					<div style={{width: `${Math.min(100, 100*totalBondExp/BOND_TABLE[100])}%`, height: "100%", backgroundColor: "var(--pink)"}}>
					</div>
				</div>
				<div style={{width: "100%", border: "1px solid", height: "0.5rem", margin: "0.5rem 0 0 0"}}>
					<div style={{ width: `${Math.min(100, 100*(totalBondExp-BOND_TABLE[endBondLv])/(BOND_TABLE[endBondLv+1]-BOND_TABLE[endBondLv]))}%`, height: "100%", backgroundColor: "var(--pink)"}}>
					</div>
				</div>
				<label className="total-bond text-muted text-start">
					{totalBondExp?.toLocaleString()}/{BOND_TABLE[100]?.toLocaleString()} [{Math.min(100, 100*totalBondExp/BOND_TABLE[100]).toFixed(2)}%]
				</label>
				<label hidden={endBondLv >= BOND_TABLE.length-2} className="next-bond text-muted text-end">
					{(totalBondExp-BOND_TABLE[endBondLv])?.toLocaleString()}/{(BOND_TABLE[endBondLv+1]-BOND_TABLE[endBondLv])?.toLocaleString()} [{Math.min(100, 100*(totalBondExp-BOND_TABLE[endBondLv])/(BOND_TABLE[endBondLv+1]-BOND_TABLE[endBondLv])).toFixed(2)}%]
				</label>
			</Col></Row>
			<Row style={{marginTop: "0.5rem"}}>
				<Col>Goal: <small>{bond?.toLocaleString()} &gt; {endBondLv?.toLocaleString()}</small> &gt; <input type="number" min={1} max={100} value={goal} style={{width: "2.5rem", padding: 0, background: "rgba(255,255,255,0.1)", alignContent: "center", textAlign: "center", border: "none"}}
					onChange={(e)=>{
						setGoal(Math.min(parseInt(e.target.value.replaceAll(/[^\d]/g,"")), 100));
					}}
				></input></Col>
				<Col hidden={endBondLv >= BOND_TABLE.length-2 || goalRemain <= 0}>
					Remain: {((goalRemain) - (totalBondExp-BOND_TABLE[endBondLv]))?.toLocaleString() }
				</Col>
			</Row>
			<Row className="progress-bar" hidden={goal>100 || bond > goal} style={{margin: "0.5rem 0 0 0", padding: "0 0 0 0"}}><Col>
				<div style={{width: "100%", border: "1px solid", height: "0.5rem", margin: "0.5rem 0 0 0"}}>
					<div style={{ width: `${Math.min(100, 100*startBondExp/(BOND_TABLE[goal]))}%`, height: "100%", backgroundColor: "var(--pink)"}}>
					</div>
				</div>
				<div style={{width: "100%", border: "1px solid", height: "0.5rem", margin: "0.5rem 0 0 0"}}>
					<div style={{width: `${Math.min(100, 100*totalBondExp/BOND_TABLE[goal])}%`, height: "100%", backgroundColor: "var(--pink)"}}>
					</div>
				</div>
				<label className="total-bond text-muted text-start">
					{startBondExp?.toLocaleString()}/{BOND_TABLE[goal]?.toLocaleString()} [{Math.min(100, 100*startBondExp/BOND_TABLE[goal]).toFixed(2)}%]
				</label>
				<label className="next-bond text-muted text-end">
					{totalBondExp?.toLocaleString()}/{BOND_TABLE[goal]?.toLocaleString()} [{Math.min(100, 100*totalBondExp/BOND_TABLE[goal]).toFixed(2)}%]
				</label>
			</Col></Row>
			<Row><Col>Gain: {gainBondExp?.toLocaleString()} {totalBondExp > BOND_TABLE[100] && (<span>(Excess: {(totalBondExp - BOND_TABLE[100])?.toLocaleString()})</span>)}</Col></Row>
			{gifts.map(gift => {
				const toGoalGift = Math.max(Math.ceil(goalRemain/calculateGiftExp(gift, student)), 0)?.toLocaleString();
				return (<Col onClick={(_)=>document.getElementById(`${student.getId()}-${gift.Id}`)?.focus()} key={gift.Id} className="gift" style={{display: "inline-block", alignContent: "center", textAlign: "center"}}>
					<Row><Col>
						<RetainOverlayTrigger
							placement="top"
							overlay={<span id={`${student.getId()}-${gift.Id}-tooltip`}>{gift.Name}<br />Need: {toGoalGift}</span>}>
								<span><Images.Icon key={gift.Id} imgUrl={getIcon(gift)} size="4rem" alt={gift.Name}></Images.Icon></span>
						</RetainOverlayTrigger>
						
					</Col></Row>
					<Row style={{marginTop: "-0.5rem"}}><Col><small>{calculateGiftExp(gift, student)}</small></Col></Row>
					<Row><Col>
					<input id={`${student.getId()}-${gift.Id}`}  placeholder={toGoalGift} type="number" min={0} value={giftCtr?.[gift.Id]} style={{maxWidth: "4rem", padding: 0, background: "rgba(255,255,255,0.1)", alignContent: "center", textAlign: "center", border: "none"}}
						onChange={(e)=>{
							const value = parseInt(e.target.value.replaceAll(/[^\d]/g,""));
							updateGiftAmount(gift.Id, value);
						}}
					></input>
					{/* <input type="number" value="0" readOnly style={{maxWidth: "4rem", alignContent: "center", textAlign: "center", border: "none", backgroundSize: "4rem", backgroundImage: `url("${getIcon(gift)}")`, maxHeight: "4rem"}}></input> */}
					</Col></Row>
				</Col>)
			})}
			{gifts?.length>0 && <hr></hr>}
			{genericGifts?.map(gift => {
				const toGoalGift = Math.max(Math.ceil(goalRemain/calculateGiftExp(gift, student)), 0)?.toLocaleString();
				return (<Col onClick={(_)=>document.getElementById(`${student.getId()}-${gift.Id}`)?.focus()} key={gift.Id} className="gift" style={{display: "inline-block", alignContent: "center", textAlign: "center"}}>
					<Row><Col>
						<RetainOverlayTrigger
							placement="top"
							overlay={<span id={`${student.getId()}-${gift.Id}-tooltip`}>{gift.Name}<br />Need: {toGoalGift}</span>}>
								<span><Images.Icon key={gift.Id} imgUrl={getIcon(gift)} size="4rem" alt={gift.Name}></Images.Icon></span>
						</RetainOverlayTrigger>
					</Col></Row>
					<Row style={{marginTop: "-0.5rem"}}><Col><small>{calculateGiftExp(gift, student)}</small></Col></Row>
					<Row><Col>
					<input id={`${student.getId()}-${gift.Id}`} placeholder={toGoalGift} type="number" min={0} value={giftCtr?.[gift.Id]} style={{maxWidth: "4rem", padding: 0, background: "rgba(255,255,255,0.1)", alignContent: "center", textAlign: "center", border: "none"}}
						onChange={(e)=>{
							const value = parseInt(e.target.value.replaceAll(/[^\d]/g,""));
							updateGiftAmount(gift.Id, value);
						}}
					></input>
					{/* <input type="number" value="0" readOnly style={{maxWidth: "4rem", alignContent: "center", textAlign: "center", border: "none", backgroundSize: "4rem", backgroundImage: `url("${getIcon(gift)}")`, maxHeight: "4rem"}}></input> */}
					</Col></Row>
				</Col>)
			})}
			{[1,2,3].map(tier=>{
				const nodes = relevantCrafts?.Nodes.filter(node=>node.Tier === tier) ?? []
				let overallG = 0;
				let overallJ = 0;
				let chanceG = 1;
				let chanceJ = 1;
				for(let i=0; i<nodes.length; i++) {
					overallG += nodes[i].AverageBondExpGlobal * nodes[i].ChanceGlobal;
					overallJ += nodes[i].AverageBondExp * nodes[i].ChanceJp;
					chanceG *= (1-nodes[i].ChanceGlobal);
					chanceJ *= (1-nodes[i].ChanceJp);
				}
				return <Col><hr></hr>
				<h6>
				<RetainOverlayTrigger
					placement="top"
					overlay={<span id={`${student.getId()}-craft-${tier}-tooltip`}>{nodes.map((node:IBondCraftNode)=>{
						return <BondCraftNode key={node.Id} node={node}></BondCraftNode>
					})}</span>}>
						<span>Craft Node {tier}: G{overallG.toFixed(2)} | J{overallJ.toFixed(2)}</span>
				</RetainOverlayTrigger></h6>
				
					{showCraftDetail && nodes.map((node:IBondCraftNode)=>{
						return <BondCraftNode key={node.Id} node={node}></BondCraftNode>
					})}
				</Col>
			})}
		</Col>)
}

function GiftCard({data, students}: {data:ISchaleGiftItem, students:SchaleStudent[]}) {
	const icon = getIcon(data);
	const taggedStudents = students.filter(s=>[...s.FavorItemTags, ...s.FavorItemUniqueTags].filter(t=> data.Tags.indexOf(t) >= 0 ).length > 0);

	return (
		<Card className="text-pink gift card" key={data.Id} style={styles.card}>
			<Card.Header style={styles.cardTitle}>
				<Card.Title className="responsive-font">
					{data.Name}
				</Card.Title>
			</Card.Header>
			<Card.Body>
				<Card.Img as={"span"}><ImageGallery title={data.Name} srcs={[icon]}></ImageGallery></Card.Img>
			</Card.Body>
			<Card.Footer style={{textAlign: "center", alignContent: "center"}}>
			{taggedStudents.map(s => [...s.FavorItemTags, ...s.FavorItemUniqueTags].filter(t=> data.Tags.indexOf(t) >= 0 )
					.map((_, i )=> <Images.StudentIcon key={`${s.getId()}_${i}`} studentId={s.getId().toString()} size="2rem" alt={s.getDisplayName()} />))
				}
			<Card.Text><small>{calculateBaseGiftExp(data)}</small></Card.Text>
			</Card.Footer>
		</Card>)
}

class LessonRank implements ITableRow {
	academyRank: number;
	areaLevel: number;
	bondExp: number;
	bonus: number;
	bonusChance: number;
	constructor(rowData: [academyRank: number, areaLevel: number, bondExp: number, bonus: number, bonusChance: number]) {
		let i=0;
		this.academyRank = rowData[i++];
		this.areaLevel = rowData[i++];
		this.bondExp = rowData[i++];
		this.bonus = rowData[i++];
		this.bonusChance = rowData[i++];
	}
	getHeaders() {
		return ["Rank", "Lv", "Bond", "Bonus", "Bonus Chance"];
	}
	toArray() {
		return [
			this.academyRank?.toLocaleString(),
			this.areaLevel?.toLocaleString(),
			this.bondExp?.toLocaleString(),
			`${this.bonus?.toLocaleString()} (${(this.bonus+this.bondExp)?.toLocaleString()})`,
			this.bonusChance?.toLocaleString()+"%",
		];
	}
	toKeyValues() {
		const headers = this.getHeaders();
		const data = this.toArray();
		let result: { [key: string]: string } = {};
		for (const i in headers) {
			result[headers[i]] = data[i] ?? '';
		}
		return result;
	}
}

const LESSON_RANK_TABLE: LessonRank[] = [
	new LessonRank([1, 1, 15, 15, 10]),
	new LessonRank([4, 2, 15, 15, 15]),
	new LessonRank([7, 3, 15, 15, 20]),
	new LessonRank([10, 4, 15, 15, 25]),
	new LessonRank([11, 5, 20, 20, 25]),
	new LessonRank([12, 6, 25, 25, 25]),
]

const HEADPAT_ITEM: ISchaleGiftItem = {
	Category: "Favor",
	ExpValue: 15,
	Tags: [],
	Id: 80420,
	Name: "Headpat",
	Desc: "",
	Icon: "item_icon_event_token_0_s38",
	Flavor: "",
	Effect: "",
	Rarity: "N",
	Quality: 0
}

const uniqueStudentSorter = (targetStudents: SchaleStudent[]) => (a:ISchaleGiftItem,b:ISchaleGiftItem) => {
	const taggedStudentsA = targetStudents.filter(s=>[...s.FavorItemTags, ...s.FavorItemUniqueTags].filter(t=> a.Tags.indexOf(t) >= 0 ).length > 0);
	const taggedStudentsB = targetStudents.filter(s=>[...s.FavorItemTags, ...s.FavorItemUniqueTags].filter(t=> b.Tags.indexOf(t) >= 0 ).length > 0);
	// console.log(taggedStudentsA, a.Name, taggedStudentsB, b.Name);

	return taggedStudentsB.length - taggedStudentsA.length;
}
const totalStudentSorter = (targetStudents: SchaleStudent[]) => (studentA:ISchaleGiftItem,studentB:ISchaleGiftItem)=>{
	const taggedStudentsA = targetStudents.filter(s=>[...s.FavorItemTags, ...s.FavorItemUniqueTags].filter(t=> studentA.Tags.indexOf(t) >= 0 ).length > 0)
		.reduce((acc, currStudent) => {
			return [...currStudent.FavorItemTags, ...currStudent.FavorItemUniqueTags].filter(t=> studentA.Tags.indexOf(t) >= 0 ).length + acc;
		}, 0)
	
	;
	const taggedStudentsB = targetStudents.filter(s=>[...s.FavorItemTags, ...s.FavorItemUniqueTags].filter(t=> studentB.Tags.indexOf(t) >= 0 ).length > 0)
	.reduce((acc, currStudent) => {
		return [...currStudent.FavorItemTags, ...currStudent.FavorItemUniqueTags].filter(t=> studentB.Tags.indexOf(t) >= 0 ).length + acc;
	}, 0);

	return taggedStudentsB - taggedStudentsA;
}
export default function GiftChecker() {
	const {schaleStudent: studentDataset} = useSchaleStudents();
	const [filters, _setFilters] = useState<StudentFilter[]>(
		(JSON.parse(localStorage.getItem(KEYS.ITEMS)) ?? []).map(s=>new StudentFilter(s))
	);
	const setFilters = (toSet) => {
		_setFilters(toSet);
		localStorage.setItem(KEYS.ITEMS, JSON.stringify(toSet.map(f=>f.name)));
	}
	// console.log(filters)
	const targetStudents = useMemo(()=>studentDataset.filter(s=>filters.filter(f=>f.getId() == s.getId()).length > 0).sort((a,b)=>filters.findIndex(v=>v.getId()===a.getId())-filters.findIndex(v=>v.getId()===b.getId())), 
		[studentDataset, filters]);

	const [gifts, setGifts] = useState<ISchaleGiftItem[]>();
	const [craft, setCraft] = useState<ISchaleCraftJson>();
	const [craftDetail, _setCraftDetail] = useState<boolean>((JSON.parse(localStorage.getItem(KEYS.CRAFT_DETAIL)) ?? true));
	const [groups, setGroups] = useState<ISchaleGroupsJson>();
	const [genericGifts, setGenericGifts] = useState<ISchaleGiftItem[]>();
	const setCraftDetail = (toSet:boolean) => {
		_setCraftDetail(toSet);
		localStorage.setItem(KEYS.CRAFT_DETAIL, JSON.stringify(toSet));
	}
	useEffect(() => {
		let ignore = false;
		async function loadInfo() {
			const awaitable = [
				getJson("https://schaledb.com/data/en/items.min.json"),
				getJson("https://schaledb.com/data/crafting.min.json"),
				getJson("https://schaledb.com/data/groups.min.json"),
			]
			const js = await awaitable[0];
			if (!ignore) {
				const temp = (Object.values(js).filter((i:any)=>i.Category === "Favor").map(i=>i as ISchaleGiftItem)
				.sort(uniqueStudentSorter(targetStudents))
				.sort(totalStudentSorter(targetStudents))
				// .sort((a,b)=>calculateBaseGiftExp(b) - calculateBaseGiftExp(a))
				);
				setGifts(temp);

				const genericIds = [5996, 100008, 100009, 5998, 100000]

				setGenericGifts((Object.values(js).filter((i:any)=>genericIds.includes(i.Id)).map(i=>{
					const item = {...(i as ISchaleGiftItem)} as ISchaleGiftItem;
					if (item.Id === 100000) {
						item.ExpValue = 20;
					} else if (item.Id === 100009) {
						item.ExpValue = 120;
					} else if (item.Id === 100008) {
						item.ExpValue = 20;
					} else if (item.Id === 5998) {
						item.Name = "Novelty Gift";
					} else if (item.Id === 5996) {
						item.Name = "Paid Gift";
					}
					item.Tags= item.Tags ?? [];
					return item;
				})
				// .sort(uniqueStudentSorter)
				// .sort(totalStudentSorter)
				.sort((a,b)=>calculateBaseGiftExp(b) - calculateBaseGiftExp(a))
				));
				
				const craft = await awaitable[1] as ISchaleCraftJson;
				setCraft({
					...craft,
					Nodes: craft.Nodes.filter((i:ISchaleCraftNode)=>["Rairty", "Favor"].includes(i.Icon))
				})
				const groups = await awaitable[2] as ISchaleGroupsJson;
				const giftIds = temp.map((gift:ISchaleGiftItem)=>gift.Id)
				for (const id in groups) {
					groups[id].Items = groups[id].Items.filter((item:ISchaleGroupItem)=>item.Type === "Item" && giftIds.includes(item.Id));
					groups[id].ItemsGlobal = groups[id].Items.filter((item:ISchaleGroupItem)=>item.Type === "Item" && giftIds.includes(item.Id));
					// groups[id].ItemsCn = groups[id].Items.filter((item:ISchaleGroupItem)=>{ item.Type === "Item");
				}
				setGroups(groups);
			}
		}

		loadInfo();

		return () => {
			ignore = true
		}
	}, [targetStudents]);
	useEffect(() => {
		let ignore = false;
		if (!ignore && gifts) {
			const temp = ([...gifts]
				.sort(uniqueStudentSorter(targetStudents))
				.sort(totalStudentSorter(targetStudents))
			);
			setGifts(temp);
			// setGifts([...gifts]);
		}
		return () => {
			ignore = true
		}
	// adding gifts dependency here causes infinite loop
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, targetStudents]);

	return (<>
	<h2 className="text-pink">Aggregate Gift Checker</h2>
	<Row>
		<Col>
		Add Students: 
		<MultiStudentPicker 
			setTarget={(toSet) => {
				setFilters(toSet ?? []);
			}} 
			target={filters}
			pop={() => {
				const r =filters.pop()
				setFilters([...filters]);

				return r;
			}}
			push={(toAdd:string) => {
				if (filters.findIndex(f=>f.name == toAdd) < 0) {
					filters.push(new StudentFilter(toAdd));
					setFilters([...new Set(filters)]);
				}
			}}
		/>
		</Col>
	</Row>
	<Row>
		<Col>
			<Form.Check 
			type="switch"
			id="craft-detail"
			label="Display detailed breakdown for Craft Nodes"
			checked={craftDetail}
			onChange={(a)=>{
				setCraftDetail(a.target.checked);
			}}
			/>
		</Col>
	</Row>
		
		<Row className="bg-violet-transparent-05 bg-pink-border rounded-border" style={{opacity: "unset"}}>
			<Col>
				<Row><Col><h3 id="Planner">Gift Planner</h3></Col></Row>
				<Row 
				// style={{alignContent: "center", textAlign: "center"}}
				>
					{targetStudents?.map((student, index)=>{
						return <StudentBondPanel showCraftDetail={craftDetail} onClose={() => {
							filters.splice(filters.findIndex(v => v.getId() === student.getId()), 1);
							setFilters([...filters]);
						} } student={student} index={index} gifts={gifts} genericGifts={genericGifts}
						craft={craft} groups={groups} key={student.getId()}
						/>
					})}
				</Row>
			</Col>
		</Row>
		<Row className="bg-violet-transparent-05 bg-pink-border rounded-border" style={{opacity: "unset"}}>
			<Row><Col><h3 id="Memo">Bond Memo</h3></Col></Row>
			<Row>
				<Col><h4 id="SR" className="text-pink">SR Gifts</h4></Col>
			</Row>
			<Row>
				<Col style={styles.cardContainer}>
					{gifts?.filter(gift=>gift.Rarity === "SR").map(gift => {
						return (<GiftCard data={gift} students={targetStudents}></GiftCard>)
					})}
				</Col>
			</Row>
			<hr></hr>
			<Row>
				<Col><h4 id="SSR" className="text-pink">SSR Gifts</h4></Col>
			</Row>
			<Row>
				<Col style={styles.cardContainer}>
					{gifts?.filter(gift=>gift.Rarity === "SSR").map(gift => {
						return (<GiftCard data={gift} students={targetStudents}></GiftCard>)
					})}
				</Col>
			</Row>
			<hr></hr>
			<Row>
				<Col><h4 id="Generic" className="text-pink">Generic Gifts</h4></Col>
			</Row>
			<Row>
				<Col style={styles.cardContainer}>
					{genericGifts?.map(gift => {
						return (<GiftCard data={gift} students={targetStudents}></GiftCard>)
					})}
				</Col>
			</Row>
			<hr></hr>
			<Row>
				<Col><h4 id="Others" className="text-pink">Others</h4></Col>
				
			</Row>
			<Row>
				<Col style={{maxWidth: "24.5rem"}}>
					<Row><Col><h5 className="text-pink">Lesson</h5></Col></Row>
					<Row><Col><MyTable data={LESSON_RANK_TABLE} variant="violet"/></Col></Row>
				</Col>
				
				<Col>
					<Row><Col><h5 className="text-pink">Cafe</h5></Col></Row>
					<Row>
				<Col style={styles.cardContainer}>
					<GiftCard data={HEADPAT_ITEM} students={targetStudents}></GiftCard>
				</Col></Row>
				</Col>
			</Row>
		</Row>

	</>);
}